import { usePresence } from "@/contexts/PresenceContext";
import { getReelRoleName, isStrictAdmin } from "@/helpers/UserHelpers";
import { UserRoleType } from "@/interfaces/IUser";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC } from "react";
import { Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

/**
 * En-tête : indicateur des utilisateurs connectés (admin uniquement).
 * Affiche un compteur dans la navbar et la liste détaillée au clic.
 * Cliquer sur une personne amène l'admin sur la même page qu'elle.
 */
const PresenceIndicator: FC = () => {
  const user = useSelector(selectCurrentUser);
  const { connectedUsers } = usePresence();
  const navigate = useNavigate();

  if (!isStrictAdmin(user)) return null;

  return (
    <Dropdown navbar={true} as="li">
      <Dropdown.Toggle
        bsPrefix="toggle"
        as={"div"}
        className="px-2 nav-link cursor-pointer position-relative"
      >
        <FontAwesomeIcon icon={faUsers} size="lg" />
        <span
          className="position-absolute badge rounded-pill bg-success"
          style={{ top: 0, left: "70%", fontSize: 9 }}
        >
          {connectedUsers.length}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-caret dropdown-menu-card dropdown-menu-end">
        <div
          className="bg-white rounded-2 py-2 dark__bg-1000"
          style={{ minWidth: 260, maxHeight: 360, overflowY: "auto" }}
        >
          <h6 className="dropdown-header py-1 fw-bold">
            En ligne ({connectedUsers.length})
          </h6>
          {connectedUsers.length === 0 ? (
            <span className="dropdown-item-text text-700 fs--1">Personne</span>
          ) : (
            connectedUsers.map((u) => (
              <Dropdown.Item
                key={u.socketId}
                as="button"
                type="button"
                disabled={!u.route}
                onClick={() => u.route && navigate(u.route)}
                title={
                  u.route ? `Aller sur la page de ${u.fullname}` : undefined
                }
                className="d-flex align-items-center gap-2 py-2"
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: u.color,
                    flex: "0 0 auto",
                  }}
                />
                <span
                  className="d-flex flex-column"
                  style={{ minWidth: 0, lineHeight: 1.25 }}
                >
                  <span className="fw-semi-bold fs--1">{u.fullname}</span>
                  <span className="text-700" style={{ fontSize: 11 }}>
                    {getReelRoleName(u.roles as UserRoleType[])} ·{" "}
                    <span>{u.label ?? "—"}</span>
                  </span>
                </span>
              </Dropdown.Item>
            ))
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default PresenceIndicator;
